import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, Check, Send, BookOpen, Shield, Eye, Heart, MessageSquare, Phone, Mail, AlertCircle, X, DollarSign, Calendar, Clock, Users, Play, ExternalLink } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/services/api';
import AcknowledgmentModal from '@/components/AcknowledgmentModal';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DateTimePickerModal from '@/components/DateTimePickerModal';

export function OrientationPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  // Check if user already acknowledged
  const hasAcknowledged = localStorage.getItem('casa_recruit_acknowledgment');
  
  // New flow: animation -> acknowledgment -> content
  const [stage, setStage] = useState(hasAcknowledged ? 'content' : 'animation');
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  
  const [formData, setFormData] = useState({
    stage_name: '',
    real_name: '',
    age_confirmed: false,
    contact_preference: 'text',
    contact_info: '',
    visit_type: '', // 'visit_casa' or 'we_visit'
    visit_datetime: '',
    visit_formatted: '',
    availability_days: '',
    availability_times: '',
    experience: '',
    referral_name: '',
    referral_contact: '',
    photo_urls: [],
  });

  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    if (stage === 'content') {
      api.trackEvent('page_view', 'orientation', '/orientation');
    }
  }, [stage]);

  // Function to handle animation completion - moved above useEffect
  const handleAnimationComplete = () => {
    setAnimationComplete(true);
    setTimeout(() => {
      setStage('acknowledgment');
    }, 500);
  };

  // Handle animation playback
  useEffect(() => {
    if (stage === 'animation' && videoRef.current) {
      const attemptPlay = async () => {
        try {
          await videoRef.current.play();
        } catch (err) {
          console.log('Autoplay prevented');
          setShowPlayButton(true);
        }
      };
      
      if (videoRef.current.readyState >= 3) {
        attemptPlay();
      } else {
        videoRef.current.addEventListener('canplay', attemptPlay, { once: true });
      }
      
      // Timeout fallback
      const timeout = setTimeout(() => {
        if (stage === 'animation') {
          handleAnimationComplete();
        }
      }, 8000);
      
      return () => clearTimeout(timeout);
    }
  }, [stage]);

  const handleManualPlay = async () => {
    setShowPlayButton(false);
    if (videoRef.current) {
      try {
        await videoRef.current.play();
      } catch (err) {
        handleAnimationComplete();
      }
    }
  };

  const handleAcknowledgmentAccept = () => {
    setStage('content');
  };

  const handleAcknowledgmentDecline = () => {
    navigate('/');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      try {
        const result = await api.uploadFile(file);
        setUploadedPhotos(prev => [...prev, { url: result.url, filename: result.filename }]);
        setFormData(prev => ({
          ...prev,
          photo_urls: [...prev.photo_urls, result.url]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        alert(`Failed to upload ${file.name}`);
      }
    }
  };

  const removePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  const handleDateTimeSelect = (selection) => {
    setFormData(prev => ({
      ...prev,
      visit_datetime: selection.isoDate,
      visit_formatted: selection.formatted,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.age_confirmed) {
      alert(language === 'en' ? 'You must confirm you are 18 or older.' : 'Debes confirmar que tienes 18 años o más.');
      return;
    }

    setIsSubmitting(true);

    try {
      await api.createApplication({
        preferred_name: formData.stage_name,
        real_name: formData.real_name || null,
        age_confirmed: formData.age_confirmed,
        contact: `${formData.contact_preference}: ${formData.contact_info}`,
        visit_type: formData.visit_type,
        visit_datetime: formData.visit_datetime,
        availability: `${formData.availability_days} / ${formData.availability_times}`,
        experience: formData.experience,
        referral: formData.referral_name ? `${formData.referral_name} - ${formData.referral_contact}` : '',
        photo_urls: formData.photo_urls,
      });
      api.trackEvent('form_submit', 'application_submit', '/orientation');
      setIsSuccess(true);
    } catch (error) {
      console.error('Application error:', error);
      alert(language === 'en' ? 'There was an error submitting your application. Please try again.' : 'Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = {
    en: {
      header: 'Brand Orientation',
      subtitle: 'Join Casa de Candy',
      introTitle: 'Orientation · Read First',
      introText: 'This page is your official introduction to The House. If you\'re reading this, you\'ve been privately invited to preview Casa de Candy. This guide explains who we are, what we stand for, and how we expect our hosts to carry themselves.',
      introSubtext: 'Casa de Candy is built on a simple equation: beauty + discipline + safety + controlled environment. We handle the atmosphere, screening, and presentation. You bring presence, confidence, and professionalism.',
      wonderingTitle: "Earnings & Split",
      wondering: [
        { icon: DollarSign, title: 'Standard Split', text: 'The standard split is 40% to you, 60% to The House. The House share funds promotion, screening, operations, security, supplies, venues, and logistics.' },
        { icon: DollarSign, title: 'Tips', text: 'Tips are split 70% to you, 30% to The House. We aim to process tips on the same day whenever operationally possible.' },
        { icon: Calendar, title: 'Flexible Scheduling', text: 'Work when it works for you. You receive advance notice so you can accept or decline.' },
        { icon: Users, title: 'We Handle The Hard Stuff', text: 'Random calls, disorganized bookings, disrespectful guests, unclear expectations. Your role is to show up composed, attractive, direct, and respectful of yourself.' },
      ],
      supportTitle: 'Optional Support Resources',
      supportNote: '(Some may cost employee)',
      supportItems: [
        'Tax/financial guidance',
        'Management coaching',
        'Credit support guidance',
        'Childcare help pathways',
      ],
      employmentType: 'Employment type choice: Employee OR Contract Labor',
      brandOrientation: {
        title: 'What We Are',
        subtitle: 'Introduction to The House',
        description: 'The House is a structured, high-standards collective focused on brand, safety, consistency, and growth. We are not a free-for-all or a chaotic party. We are a carefully controlled environment where image, timing, and experience matter.',
        items: [
          { icon: Heart, title: 'What We Focus On', text: 'Paid time in a curated, neon-lit environment. Visual presence, conversation, and atmosphere. Non-explicit companionship and hosting.' },
          { icon: Shield, title: 'What We Don\'t Do', text: 'We do not arrange, promise, or encourage anything illegal. We do not sell or market explicit acts. Boundaries are set by you and enforced by The House.' },
          { icon: Eye, title: 'Our Promise', text: 'We don\'t ask you to change who you are. We amplify you with system, discipline, and discretion.' },
        ],
      },
      privacy: {
        title: 'Privacy & Identity Protection',
        items: [
          'Stage names are used instead of real names',
          'Profiles are built with controlled images and minimal personal details',
          'Guest communication runs through House channels — not your personal number',
          'Any additional content you choose to create is opt-in, not required',
        ],
      },
      safety: {
        title: 'Safety Protocols',
        subtitle: 'Your Safety Comes First',
        items: [
          'Hosts always have the right to end an interaction if they feel disrespected or unsafe',
          'Private and living areas stay separate from any guest-facing space',
          'We encourage check-in messages and clear arrival/exit timestamps',
          'If something doesn\'t feel right, we\'d rather you walk away than "push through it"',
        ],
        note: 'If a guest crosses a line, the interaction ends. The House steps in to handle logistics, communication, and any future decisions regarding that guest.',
      },
      rules: {
        title: 'Standards & Accountability',
        subtitle: 'The Core 10',
        items: [
          'Show up on time and ready — reliability is the foundation of everything',
          'Honor the schedule blocks you agree to — respect everyone\'s time',
          'Keep communication sharp, direct, and respectful',
          'No gossip, no screenshots, no drama loops',
          'Discretion is paramount — client data stays confidential',
          'Brand standards — presentation and conduct must reflect Casa de Candy values',
          'No competing interests — loyalty to the system, no client redirection',
          'Zero tolerance for dishonesty or boundary violations',
          'A pattern of dishonesty or brand damage ends the relationship',
          'Trust is our currency — we protect it fiercely',
        ],
      },
      trial: {
        title: 'Preview / Trial Run',
        description: 'You don\'t have to commit long-term to understand how Casa de Candy works. We allow a light "trial" entry so you can see the flow.',
        items: [
          'You provide three photos — face optional',
          'If your face appears, we can mask it without altering your overall look',
          'We assign a stage name and build a controlled profile',
          'When a request comes in, you receive advance notice to accept or decline',
        ],
      },
      application: {
        title: 'Start Your Journey',
        subtitle: 'Apply / Submit for Review',
        form: {
          photos: 'Upload Photos (Optional)',
          photosHint: 'Upload clear, recent photos. Multiple images accepted.',
          stageName: 'Stage Name',
          stageNameHint: 'Required',
          realName: 'Real Name',
          realNameHint: 'Optional - for internal records only',
          age: 'I confirm I am 18 years or older',
          contactPref: 'Preferred Contact Method',
          contactInfo: 'Contact Info',
          visitType: 'Schedule Your House Visit',
          visitCasa: 'Visit Casa',
          weVisit: 'We Visit You',
          selectDateTime: 'Select Date & Time',
          availabilityDays: 'Availability (Days)',
          availabilityTimes: 'Availability (Times)',
          experience: 'Experience Level',
          referral: 'Referred by a friend?',
          referralName: "Friend's Name",
          referralContact: "Friend's Contact",
          submit: 'Submit Application',
        },
        daysOptions: ['Weekdays', 'Weekends', 'Any Day'],
        timesOptions: ['Morning', 'Afternoon', 'Evening', 'Overnight', 'Any Time'],
        experienceOptions: ['None / New', 'Some Experience'],
      },
      handbook: {
        title: 'Employee Handbook',
        subtitle: 'Review our complete operational guidelines, standards, and expectations',
        cta: 'Open Employee Handbook',
        attribution: 'Instruction by Casa de Candy',
      },
      success: {
        title: 'Application Received!',
        message: 'Thank you for your interest in Casa de Candy. We will review your application and contact you within 24-48 hours.',
        nextSteps: 'Next Steps',
        steps: [
          'Wait for our review (24-48 hours)',
          'Brief screening call',
          'Trial session scheduling',
        ],
      },
    },
    es: {
      header: 'Orientación de Marca',
      subtitle: 'Únete a Casa de Candy',
      introTitle: 'Orientación · Lee Primero',
      introText: 'Esta página es tu introducción oficial a La Casa. Si estás leyendo esto, has sido invitada privadamente a conocer Casa de Candy. Esta guía explica quiénes somos, qué representamos y cómo esperamos que nuestras anfitrionas se conduzcan.',
      introSubtext: 'Casa de Candy se basa en una ecuación simple: belleza + disciplina + seguridad + ambiente controlado. Nosotros manejamos el ambiente, filtrado y presentación. Tú traes presencia, confianza y profesionalismo.',
      wonderingTitle: "Ganancias y División",
      wondering: [
        { icon: DollarSign, title: 'División Estándar', text: 'La división estándar es 40% para ti, 60% para La Casa. La parte de La Casa financia promoción, filtrado, operaciones, seguridad, suministros y logística.' },
        { icon: DollarSign, title: 'Propinas', text: 'Las propinas se dividen 70% para ti, 30% para La Casa. Procesamos propinas el mismo día cuando es posible.' },
        { icon: Calendar, title: 'Horario Flexible', text: 'Trabaja cuando te convenga. Recibes aviso previo para aceptar o rechazar.' },
        { icon: Users, title: 'Nosotros Manejamos Lo Difícil', text: 'Llamadas random, reservas desorganizadas, huéspedes irrespetuosos, expectativas confusas. Tu rol es presentarte compuesta, atractiva, directa y con respeto propio.' },
      ],
      supportTitle: 'Recursos de Apoyo Opcionales',
      supportNote: '(Algunos pueden tener costo)',
      supportItems: [
        'Orientación fiscal/financiera',
        'Coaching de gestión',
        'Orientación de crédito',
        'Opciones de ayuda con cuidado infantil',
      ],
      employmentType: 'Tipo de empleo a elegir: Empleada O Trabajo por Contrato',
      brandOrientation: {
        title: 'Lo Que Somos',
        subtitle: 'Introducción a La Casa',
        description: 'La Casa es un colectivo estructurado de altos estándares enfocado en marca, seguridad, consistencia y crecimiento. No somos un desorden ni una fiesta caótica. Somos un ambiente cuidadosamente controlado donde la imagen, el tiempo y la experiencia importan.',
        items: [
          { icon: Heart, title: 'En Qué Nos Enfocamos', text: 'Tiempo pagado en un ambiente curado con neón. Presencia visual, conversación y ambiente. Compañía y hosting no explícito.' },
          { icon: Shield, title: 'Lo Que No Hacemos', text: 'No arreglamos, prometemos ni fomentamos nada ilegal. No vendemos ni comercializamos actos explícitos. Los límites los pones tú y los hace cumplir La Casa.' },
          { icon: Eye, title: 'Nuestra Promesa', text: 'No te pedimos que cambies quién eres. Te amplificamos con sistema, disciplina y discreción.' },
        ],
      },
      privacy: {
        title: 'Privacidad y Protección de Identidad',
        items: [
          'Se usan nombres artísticos en lugar de nombres reales',
          'Los perfiles se construyen con imágenes controladas y mínimos detalles personales',
          'La comunicación con huéspedes pasa por canales de La Casa — no tu número personal',
          'Cualquier contenido adicional que elijas crear es opcional, no requerido',
        ],
      },
      safety: {
        title: 'Protocolos de Seguridad',
        subtitle: 'Tu Seguridad es Primero',
        items: [
          'Las anfitrionas siempre tienen derecho a terminar una interacción si se sienten irrespetadas o inseguras',
          'Las áreas privadas y de vivienda se mantienen separadas de cualquier espacio para huéspedes',
          'Fomentamos mensajes de check-in y marcas claras de entrada/salida',
          'Si algo no se siente bien, preferimos que te retires a que "aguantes"',
        ],
        note: 'Si un huésped cruza una línea, la interacción termina. La Casa se encarga de la logística, comunicación y cualquier decisión futura respecto a ese huésped.',
      },
      rules: {
        title: 'Estándares y Responsabilidad',
        subtitle: 'Los 10 Fundamentales',
        items: [
          'Llega a tiempo y lista — la confiabilidad es la base de todo',
          'Honra los bloques de horario que aceptas — respeta el tiempo de todos',
          'Mantén la comunicación directa, clara y respetuosa',
          'Sin chismes, sin capturas de pantalla, sin dramas',
          'La discreción es primordial — los datos de clientes son confidenciales',
          'Estándares de marca — presentación y conducta deben reflejar valores de Casa de Candy',
          'Sin intereses en competencia — lealtad al sistema, sin redirección de clientes',
          'Tolerancia cero para deshonestidad o violaciones de límites',
          'Un patrón de deshonestidad o daño a la marca termina la relación',
          'La confianza es nuestra moneda — la protegemos ferozmente',
        ],
      },
      trial: {
        title: 'Vista Previa / Prueba',
        description: 'No tienes que comprometerte a largo plazo para entender cómo funciona Casa de Candy. Permitimos una entrada de "prueba" ligera para que veas el flujo.',
        items: [
          'Provees tres fotos — cara opcional',
          'Si tu cara aparece, podemos enmascararla sin alterar tu look general',
          'Asignamos un nombre artístico y construimos un perfil controlado',
          'Cuando llega una solicitud, recibes aviso previo para aceptar o rechazar',
        ],
      },
      application: {
        title: 'Comienza Tu Camino',
        subtitle: 'Aplicar / Enviar para Revisión',
        form: {
          photos: 'Subir Fotos (Opcional)',
          photosHint: 'Sube fotos claras y recientes. Se aceptan múltiples imágenes.',
          stageName: 'Nombre Artístico',
          stageNameHint: 'Requerido',
          realName: 'Nombre Real',
          realNameHint: 'Opcional - solo para registros internos',
          age: 'Confirmo que tengo 18 años o más',
          contactPref: 'Método de Contacto Preferido',
          contactInfo: 'Información de Contacto',
          visitType: 'Programa Tu Visita',
          visitCasa: 'Visitar Casa',
          weVisit: 'Te Visitamos',
          selectDateTime: 'Seleccionar Fecha y Hora',
          availabilityDays: 'Disponibilidad (Días)',
          availabilityTimes: 'Disponibilidad (Horarios)',
          experience: 'Nivel de Experiencia',
          referral: '¿Referida por una amiga?',
          referralName: 'Nombre de Amiga',
          referralContact: 'Contacto de Amiga',
          submit: 'Enviar Solicitud',
        },
        daysOptions: ['Entre Semana', 'Fines de Semana', 'Cualquier Día'],
        timesOptions: ['Mañana', 'Tarde', 'Noche', 'Madrugada', 'Cualquier Hora'],
        experienceOptions: ['Ninguna / Nueva', 'Algo de Experiencia'],
      },
      handbook: {
        title: 'Manual del Empleado',
        subtitle: 'Revisa nuestras guías operativas completas, estándares y expectativas',
        cta: 'Abrir Manual del Empleado',
        attribution: 'Instrucción por Casa de Candy',
      },
      success: {
        title: '¡Solicitud Recibida!',
        message: 'Gracias por tu interés en Casa de Candy. Revisaremos tu solicitud y te contactaremos en 24-48 horas.',
        nextSteps: 'Siguientes Pasos',
        steps: [
          'Espera nuestra revisión (24-48 horas)',
          'Breve llamada de evaluación',
          'Programación de sesión de prueba',
        ],
      },
    },
  };

  const t = content[language];

  // Show animation FIRST when coming from hub
  if (stage === 'animation') {
    return (
      <div 
        className={`fixed inset-0 z-50 bg-[#07060B] flex items-center justify-center overflow-hidden transition-opacity duration-500 ${
          animationComplete ? 'opacity-0' : 'opacity-100'
        }`}
        data-testid="orientation-animation"
      >
        <video
          ref={videoRef}
          src={siteConfig.animations.page}
          className="w-full h-full object-contain max-w-5xl"
          playsInline
          muted
          preload="auto"
          onEnded={handleAnimationComplete}
          onError={handleAnimationComplete}
        />
        
        {showPlayButton && (
          <button
            onClick={handleManualPlay}
            className="absolute inset-0 flex flex-col items-center justify-center bg-[#07060B]/90 cursor-pointer group"
            data-testid="play-animation-btn"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#FF4DA6] to-[#8B5CFF] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#FF4DA6]/30">
              <Play className="w-10 h-10 text-white ml-1" />
            </div>
            <p className="mt-4 text-[#A7A3BA] text-sm">Tap to play</p>
          </button>
        )}
        
        {!showPlayButton && (
          <button
            onClick={handleAnimationComplete}
            className="absolute bottom-8 right-8 px-4 py-2 text-sm text-[#A7A3BA] hover:text-[#F4F2FF] transition-colors opacity-0 animate-[fadeIn_0.5s_ease-out_2s_forwards]"
          >
            Skip →
          </button>
        )}
      </div>
    );
  }

  // Show acknowledgment after animation
  if (stage === 'acknowledgment') {
    return (
      <>
        <Helmet>
          <title>{siteConfig.seo.orientation.title}</title>
        </Helmet>
        <div className="min-h-screen bg-[#07060B]">
          <AcknowledgmentModal
            type="recruit"
            storageKey="casa_recruit_acknowledgment"
            onAccept={handleAcknowledgmentAccept}
            onDecline={handleAcknowledgmentDecline}
          />
        </div>
      </>
    );
  }

  if (isSuccess) {
    return (
      <>
        <Helmet>
          <title>Application Received | Casa de Candy</title>
        </Helmet>
        <div className="min-h-screen bg-[#07060B] flex flex-col animate-[fadeIn_0.8s_ease-out]">
          <Navbar />
          <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-12">
            <div className="neon-card-glow text-center py-12 max-w-md w-full" data-testid="application-success">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#27D7FF] to-[#8B5CFF] flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#F4F2FF] mb-3">
                {t.success.title}
              </h3>
              <p className="text-[#A7A3BA] mb-8">
                {t.success.message}
              </p>
              <div className="neon-card bg-[#0B0A12] text-left">
                <h4 className="font-heading font-semibold text-[#F4F2FF] mb-3">
                  {t.success.nextSteps}
                </h4>
                <ul className="space-y-2">
                  {t.success.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#A7A3BA]">
                      <span className="text-[#FF4DA6]">{idx + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/"
                className="btn-neon btn-neon-outline mt-6 inline-flex"
              >
                {language === 'en' ? 'Back to Home' : 'Volver al Inicio'}
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Recruit Orientation | Casa de Candy</title>
        <meta name="description" content={siteConfig.seo.orientation.description} />
        <html lang={language} />
      </Helmet>

      <div className="min-h-screen bg-[#07060B] flex flex-col animate-[fadeIn_0.8s_ease-out]" data-testid="orientation-page">
        <Navbar />
        
        <main className="flex-1 pt-24">
          {/* Hero Section */}
          <section id="about" className="py-12 px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              {/* Logo */}
              <div className="flex justify-center mb-6">
                <div className="relative animate-breathe">
                  <div className="absolute inset-0 -m-8 bg-gradient-radial from-[#8B5CFF]/25 via-transparent to-transparent blur-xl" />
                  <img 
                    src={siteConfig.brand.logoUrl} 
                    alt="Casa de Candy"
                    className="w-48 sm:w-56 h-auto object-contain relative z-10"
                    style={{
                      filter: 'drop-shadow(0 0 15px rgba(139, 92, 255, 0.8)) drop-shadow(0 0 30px rgba(255, 77, 166, 0.5))'
                    }}
                  />
                </div>
              </div>
              <h1 
                className="font-heading text-4xl sm:text-5xl font-bold text-gradient-pink mb-4"
                data-testid="orientation-hero-title"
              >
                {t.header}
              </h1>
              <p className="text-[#A7A3BA] text-lg mb-8">
                {t.subtitle}
              </p>
              
              {/* Intro Card */}
              <div className="neon-card-glow text-left max-w-2xl mx-auto">
                <h3 className="font-heading text-xl font-semibold text-[#FF4DA6] mb-3">
                  {t.introTitle}
                </h3>
                <p className="text-[#F4F2FF] text-sm mb-4 leading-relaxed">
                  {t.introText}
                </p>
                <p className="text-[#A7A3BA] text-sm leading-relaxed">
                  {t.introSubtext}
                </p>
              </div>
            </div>
          </section>

          {/* What You're Probably Wondering - Pre-Section */}
          <section id="expectations" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="wondering-section">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF] mb-4">
                  {t.wonderingTitle}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {t.wondering.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="neon-card flex gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#27D7FF]/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#27D7FF]" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-[#F4F2FF] mb-1">{item.title}</h3>
                        <p className="text-[#A7A3BA] text-sm">{item.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Support Resources */}
              <div className="neon-card">
                <h3 className="font-heading font-semibold text-[#F4F2FF] mb-1">
                  {t.supportTitle}
                </h3>
                <p className="text-[#A7A3BA] text-xs mb-4">{t.supportNote}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {t.supportItems.map((item, idx) => (
                    <div key={idx} className="text-center p-3 rounded-xl bg-[#0B0A12] border border-white/5">
                      <span className="text-[#A7A3BA] text-xs">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[#8B5CFF] text-sm mt-6 text-center font-medium">
                  {t.employmentType}
                </p>
              </div>
            </div>
          </section>

          <div className="neon-divider" />

          {/* Brand Orientation Section */}
          <section id="orientation" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="brand-orientation-section">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#FF4DA6] text-sm font-semibold uppercase tracking-widest mb-2">
                  {t.brandOrientation.subtitle}
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F4F2FF] mb-4">
                  {t.brandOrientation.title}
                </h2>
                <p className="text-[#A7A3BA] max-w-2xl mx-auto">
                  {t.brandOrientation.description}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {t.brandOrientation.items.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="neon-card text-center" data-testid={`brand-item-${idx}`}>
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF4DA6]/20 to-[#8B5CFF]/20 flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-[#FF4DA6]" />
                      </div>
                      <h3 className="font-heading font-semibold text-[#F4F2FF] mb-2">{item.title}</h3>
                      <p className="text-[#A7A3BA] text-sm leading-relaxed">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Privacy & Identity Protection Section */}
          <section id="privacy" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="privacy-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF]">
                  {t.privacy.title}
                </h2>
              </div>
              <div className="neon-card border-[#8B5CFF]/20">
                <ul className="space-y-4">
                  {t.privacy.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#8B5CFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Eye className="w-3 h-3 text-[#8B5CFF]" />
                      </div>
                      <span className="text-[#F4F2FF] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <div className="neon-divider" />

          {/* Safety Protocols Section */}
          <section id="safety" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="safety-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#FF4DA6] text-sm font-semibold uppercase tracking-widest mb-2">
                  {t.safety.subtitle}
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF]">
                  {t.safety.title}
                </h2>
              </div>
              <div className="neon-card border-[#FF4DA6]/20">
                <ul className="space-y-4 mb-6">
                  {t.safety.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#FF4DA6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Shield className="w-3 h-3 text-[#FF4DA6]" />
                      </div>
                      <span className="text-[#F4F2FF] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-[#A7A3BA] text-sm italic">{t.safety.note}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Preview / Trial Run Section */}
          <section id="trial" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="trial-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF] mb-4">
                  {t.trial.title}
                </h2>
                <p className="text-[#A7A3BA]">{t.trial.description}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t.trial.items.map((item, idx) => (
                  <div key={idx} className="neon-card flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#27D7FF]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-[#27D7FF]">{idx + 1}</span>
                    </div>
                    <span className="text-[#F4F2FF] text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="neon-divider" />

          {/* Rules & Commitments Section */}
          <section id="rules" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="rules-section">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#27D7FF] text-sm font-semibold uppercase tracking-widest mb-2">
                  {t.rules.subtitle}
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F4F2FF]">
                  {t.rules.title}
                </h2>
              </div>
              <div className="neon-card border-[#27D7FF]/20">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="w-10 h-10 rounded-full bg-[#27D7FF]/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-[#27D7FF]" />
                  </div>
                  <span className="text-[#27D7FF] font-semibold uppercase tracking-wider text-sm">
                    {t.rules.subtitle}
                  </span>
                </div>
                <ul className="space-y-4">
                  {t.rules.items.map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-3" data-testid={`rule-${idx}`}>
                      <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#27D7FF]/20 to-[#8B5CFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-[#27D7FF]">{idx + 1}</span>
                      </span>
                      <span className="text-[#F4F2FF] text-sm">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <div className="neon-divider" />

          {/* Application Form Section */}
          <section id="apply" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0A12]" data-testid="application-section">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#8B5CFF] text-sm font-semibold uppercase tracking-widest mb-2">
                  {t.application.subtitle}
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F4F2FF]">
                  {t.application.title}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="neon-card" data-testid="application-form">
                <div className="space-y-6">
                  {/* Stage Name */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      {t.application.form.stageName} <span className="text-[#FF4DA6]">*</span>
                    </label>
                    <input
                      type="text"
                      name="stage_name"
                      value={formData.stage_name}
                      onChange={handleChange}
                      required
                      className="input-neon"
                      placeholder={t.application.form.stageNameHint}
                      data-testid="app-stage-name-input"
                    />
                  </div>

                  {/* Real Name (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      {t.application.form.realName}
                    </label>
                    <input
                      type="text"
                      name="real_name"
                      value={formData.real_name}
                      onChange={handleChange}
                      className="input-neon"
                      placeholder={t.application.form.realNameHint}
                      data-testid="app-real-name-input"
                    />
                  </div>

                  {/* Age Confirmation */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-[#111022] border border-[#FF4DA6]/20">
                    <input
                      type="checkbox"
                      name="age_confirmed"
                      checked={formData.age_confirmed}
                      onChange={handleChange}
                      required
                      className="mt-1 w-5 h-5 rounded border-[#8B5CFF] bg-[#0B0A12] text-[#FF4DA6] focus:ring-[#FF4DA6]"
                      data-testid="app-age-checkbox"
                    />
                    <label className="text-[#F4F2FF] text-sm font-medium">
                      {t.application.form.age} <span className="text-[#FF4DA6]">*</span>
                    </label>
                  </div>

                  {/* Contact Preference */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-3">
                      {t.application.form.contactPref} <span className="text-[#FF4DA6]">*</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {['call', 'text', 'email', 'whatsapp'].map((pref) => (
                        <button
                          key={pref}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, contact_preference: pref }))}
                          className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                            formData.contact_preference === pref
                              ? 'bg-gradient-to-r from-[#FF4DA6]/20 to-[#8B5CFF]/20 border-[#FF4DA6] text-[#FF4DA6]'
                              : 'bg-[#0B0A12] border-white/10 text-[#A7A3BA] hover:border-[#FF4DA6]/50'
                          }`}
                          data-testid={`app-contact-${pref}`}
                        >
                          {pref === 'call' && <Phone className="w-4 h-4" />}
                          {pref === 'text' && <MessageSquare className="w-4 h-4" />}
                          {pref === 'email' && <Mail className="w-4 h-4" />}
                          {pref === 'whatsapp' && <MessageSquare className="w-4 h-4" />}
                          <span className="capitalize">{pref === 'whatsapp' ? 'WhatsApp' : pref}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      {t.application.form.contactInfo} <span className="text-[#FF4DA6]">*</span>
                    </label>
                    <input
                      type={formData.contact_preference === 'email' ? 'email' : 'tel'}
                      name="contact_info"
                      value={formData.contact_info}
                      onChange={handleChange}
                      required
                      className="input-neon"
                      placeholder={formData.contact_preference === 'email' ? 'your@email.com' : '(555) 555-5555'}
                      data-testid="app-contact-info-input"
                    />
                  </div>

                  {/* Visit Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-3">
                      {t.application.form.visitType}
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, visit_type: 'visit_casa' }))}
                        className={`py-4 px-4 rounded-xl border text-sm font-medium transition-all ${
                          formData.visit_type === 'visit_casa'
                            ? 'bg-gradient-to-r from-[#27D7FF]/20 to-[#8B5CFF]/20 border-[#27D7FF] text-[#27D7FF]'
                            : 'bg-[#0B0A12] border-white/10 text-[#A7A3BA] hover:border-[#27D7FF]/50'
                        }`}
                        data-testid="app-visit-casa"
                      >
                        {t.application.form.visitCasa}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, visit_type: 'we_visit' }))}
                        className={`py-4 px-4 rounded-xl border text-sm font-medium transition-all ${
                          formData.visit_type === 'we_visit'
                            ? 'bg-gradient-to-r from-[#27D7FF]/20 to-[#8B5CFF]/20 border-[#27D7FF] text-[#27D7FF]'
                            : 'bg-[#0B0A12] border-white/10 text-[#A7A3BA] hover:border-[#27D7FF]/50'
                        }`}
                        data-testid="app-we-visit"
                      >
                        {t.application.form.weVisit}
                      </button>
                    </div>
                  </div>

                  {/* Date/Time Selection (if visit type selected) */}
                  {formData.visit_type && (
                    <div>
                      <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                        {t.application.form.selectDateTime}
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowDatePicker(true)}
                        className="input-neon w-full text-left flex items-center justify-between cursor-pointer"
                        data-testid="app-datetime-btn"
                      >
                        <span className={formData.visit_formatted ? 'text-[#F4F2FF]' : 'text-[#A7A3BA]'}>
                          {formData.visit_formatted || t.application.form.selectDateTime}
                        </span>
                        <Calendar className="w-5 h-5 text-[#8B5CFF]" />
                      </button>
                    </div>
                  )}

                  {/* Availability - Dropdowns */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                        {t.application.form.availabilityDays}
                      </label>
                      <select
                        name="availability_days"
                        value={formData.availability_days}
                        onChange={handleChange}
                        className="input-neon"
                        data-testid="app-availability-days"
                      >
                        <option value="">{language === 'en' ? 'Select...' : 'Seleccionar...'}</option>
                        {t.application.daysOptions.map((opt, idx) => (
                          <option key={idx} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                        {t.application.form.availabilityTimes}
                      </label>
                      <select
                        name="availability_times"
                        value={formData.availability_times}
                        onChange={handleChange}
                        className="input-neon"
                        data-testid="app-availability-times"
                      >
                        <option value="">{language === 'en' ? 'Select...' : 'Seleccionar...'}</option>
                        {t.application.timesOptions.map((opt, idx) => (
                          <option key={idx} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Experience */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      {t.application.form.experience}
                    </label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className="input-neon"
                      data-testid="app-experience-select"
                    >
                      <option value="">{language === 'en' ? 'Select...' : 'Seleccionar...'}</option>
                      {t.application.experienceOptions.map((opt, idx) => (
                        <option key={idx} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Referral */}
                  <div className="neon-card bg-[#0B0A12] border-[#8B5CFF]/20">
                    <p className="text-[#A7A3BA] text-sm mb-4">{t.application.form.referral}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#A7A3BA] mb-1">
                          {t.application.form.referralName}
                        </label>
                        <input
                          type="text"
                          name="referral_name"
                          value={formData.referral_name}
                          onChange={handleChange}
                          className="input-neon text-sm"
                          data-testid="app-referral-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#A7A3BA] mb-1">
                          {t.application.form.referralContact}
                        </label>
                        <input
                          type="tel"
                          name="referral_contact"
                          value={formData.referral_contact}
                          onChange={handleChange}
                          className="input-neon text-sm"
                          data-testid="app-referral-contact"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Photo Upload (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-[#A7A3BA] mb-2">
                      {t.application.form.photos}
                    </label>
                    <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-[#FF4DA6]/50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                        id="photo-upload"
                        data-testid="app-photo-input"
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <Upload className="w-10 h-10 text-[#8B5CFF] mx-auto mb-3" />
                        <p className="text-[#A7A3BA] text-sm">{t.application.form.photosHint}</p>
                      </label>
                    </div>
                    {uploadedPhotos.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {uploadedPhotos.map((photo, idx) => (
                          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-[#0B0A12] group">
                            <img src={photo.url} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removePhoto(idx)}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-neon btn-neon-pink w-full"
                    data-testid="app-submit-btn"
                  >
                    {isSubmitting ? (
                      <span>{language === 'en' ? 'Submitting...' : 'Enviando...'}</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>{t.application.form.submit}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>

          {/* Employee Handbook Section */}
          <section id="handbook" className="py-16 px-4 sm:px-6 lg:px-8" data-testid="handbook-section">
            <div className="max-w-2xl mx-auto text-center">
              <div className="neon-card-glow">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#8B5CFF]/20 to-[#27D7FF]/20 flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-10 h-10 text-[#8B5CFF]" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4F2FF] mb-3">
                  {t.handbook.title}
                </h2>
                <p className="text-[#A7A3BA] mb-2 max-w-md mx-auto">
                  {t.handbook.subtitle}
                </p>
                <p className="text-[#8B5CFF] text-xs mb-8">
                  {t.handbook.attribution}
                </p>
                <a
                  href="https://customer-assets.emergentagent.com/job_97103200-75f7-4936-80be-6484367032ae/artifacts/vpamzufu_casa_de_candy_employee_handbook_v1.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-neon btn-neon-cyan inline-flex"
                  data-testid="open-handbook-btn"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>{t.handbook.cta}</span>
                  <ExternalLink className="w-3 h-3 ml-1 opacity-70" />
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />

        {/* Date/Time Picker Modal */}
        <DateTimePickerModal
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          onSelect={handleDateTimeSelect}
          showServiceSelect={false}
        />
      </div>
    </>
  );
}

export default OrientationPage;
